import { Component, useEffect, useState } from "react";
import {
  X, PlayCircle, StopCircle, CheckCircle2, XCircle, Loader2, Plus, Send,
  History, Wallet, Settings as SettingsIcon, Clock, ClipboardList, Package,
  ChevronDown, ChevronUp, Sparkles, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  getCredits, listCreditOperations, listCreditTopups,
  startWorkSession, endWorkSession, cancelWorkSession,
  adminTopup, adjustCredits, approveTopup, rejectTopup,
  adminResetCredits,
  addSessionActivity, listSessionActivities,
  AI_COST_MULTIPLIER, CREDIT_TO_NIS, formatCredits, formatNis, creditsToNis,
  TOPUP_METHODS,
} from "../lib/credits";
import { api } from "../lib/api";
import { WhatsAppIcon } from "../pages/fullframe/WhatsAppIcon";
import { packageLabel } from "../lib/packages";
import BackupsPanel from "./BackupsPanel";
import SmartBackupsAdminPanel from "./SmartBackupsAdminPanel";

/**
 * Super-admin work & credit management popup, opened from each company's
 * cube on `/S.ADMIN`. Centralises every super-admin-only action so the
 * client never sees the underlying ×3 math:
 *   - Pending top-up approvals for THIS company
 *   - Start / End / Cancel work-session
 *   - Manual top-up + manual adjustment
 *   - Full operations + top-ups history (super-admin view)
 */
export function WorkCreditsModal({ company, onClose, onChanged, defaultTab = "session" }) {
  const [balance, setBalance] = useState(null);
  const [topups, setTopups] = useState([]);
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(defaultTab);

  const cid = company?.id;

  const reload = async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const [b, tps, op] = await Promise.all([
        getCredits(cid),
        listCreditTopups(cid, 100),
        listCreditOperations(cid, 100),
      ]);
      setBalance(b);
      setTopups(tps);
      setOps(op);
      onChanged?.();
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cid]);

  if (!company) return null;
  const pendingTopups = topups.filter((t) => t.status === "pending");
  const session = balance?.active_work_session || null;
  const balanceNum = balance?.credit_balance ?? 0;

  return (
    <div className="fixed inset-0 z-[10002] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-stone-900 rounded-2xl border border-fuchsia-500/30 w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} dir="rtl" data-testid="work-credits-modal">
        <header className="flex items-center justify-between p-5 border-b border-white/10 bg-gradient-to-l from-fuchsia-950/40 to-indigo-950/40 flex-shrink-0">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="h-12 w-12 object-contain flex-shrink-0" />
            ) : (
              <div className="h-12 w-12 rounded-xl bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center text-fuchsia-200 font-extrabold text-lg flex-shrink-0">
                {(company.name || "?").charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <h3 className="font-heading text-xl font-extrabold text-white truncate">
                {company.display_name || company.name}
              </h3>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                {company.contact_name && (
                  <span className="text-xs text-stone-300 font-bold truncate">{company.contact_name}</span>
                )}
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5 bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-200" data-testid="modal-package-badge">
                  <Package className="h-3 w-3" /> {packageLabel(company.package)}
                </span>
                {(company.phone || company.client_whatsapp) && (
                  <a
                    href={`https://wa.me/${(company.client_whatsapp || company.phone || "").replace(/[^\d+]/g, "").replace(/^0/, "972")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 transition"
                    data-testid="modal-whatsapp-link"
                    title={`וואטסאפ ${company.contact_name || ""}`.trim()}
                  >
                    <WhatsAppIcon size={12} /> וואטסאפ
                  </a>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg text-stone-300 hover:bg-white/10 inline-flex items-center justify-center" data-testid="work-credits-close">
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Balance strip */}
        <div className="px-5 py-3 border-b border-white/10 bg-stone-900 flex items-center justify-between gap-3 flex-shrink-0" data-testid="modal-balance-strip">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-fuchsia-400" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">יתרת הלקוח</div>
              <div className="flex items-baseline gap-1.5">
                <span className={`font-heading text-2xl font-extrabold ${balanceNum < 0 ? "text-rose-300" : balanceNum < 20 ? "text-amber-300" : "text-white"}`} data-testid="modal-balance">
                  {formatCredits(balanceNum)}
                </span>
                <span className="text-[11px] text-stone-400">≈ {formatNis(creditsToNis(balanceNum))}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1.5 text-[10px]">
            <div className="rounded-md bg-emerald-950/40 border border-emerald-500/20 px-2 py-1 text-center">
              <div className="text-stone-400">סך נטען</div>
              <div className="font-bold text-emerald-300">+{formatCredits(balance?.total_topped_up || 0)}</div>
            </div>
            <div className="rounded-md bg-rose-950/40 border border-rose-500/20 px-2 py-1 text-center">
              <div className="text-stone-400">סך חויב</div>
              <div className="font-bold text-rose-300">−{formatCredits(balance?.total_ai_cost_charged || 0)}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 bg-stone-900 overflow-x-auto flex-shrink-0">
          <TabBtn id="session" tab={tab} setTab={setTab} label={session ? "סשן פעיל ●" : "ניהול סשן"} tone={session ? "text-emerald-300" : "text-fuchsia-300"} />
          <TabBtn id="approvals" tab={tab} setTab={setTab} label={`אישור טעינות${pendingTopups.length ? ` (${pendingTopups.length})` : ""}`} tone="text-amber-300" />
          <TabBtn id="manual" tab={tab} setTab={setTab} label="פעולות ידניות" tone="text-stone-300" />
          <TabBtn id="brief" tab={tab} setTab={setTab} label="אפיון לקוח" tone="text-cyan-300" />
          <TabBtn id="history" tab={tab} setTab={setTab} label="חיובים ופעולות" tone="text-stone-300" />
          <TabBtn id="backups" tab={tab} setTab={setTab} label="גיבויים" tone="text-fuchsia-300" />
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin inline text-stone-400" /></div>
          ) : (
            <>
              {tab === "session" && (
                <SessionTab session={session} companyId={cid} onChange={reload} onClose={onClose} />
              )}
              {tab === "approvals" && (
                <ApprovalsTab pending={pendingTopups} onChange={reload} />
              )}
              {tab === "manual" && (
                <ManualTab companyId={cid} onChange={reload} />
              )}
              {tab === "brief" && (
                <BriefTabSafe companyId={cid} company={company} onChanged={reload} />
              )}
              {tab === "history" && (
                <HistoryTab ops={ops} topups={topups} />
              )}
              {tab === "backups" && (
                <SmartBackupsAdminPanel companyId={cid} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ id, tab, setTab, label, tone }) {
  const active = tab === id;
  return (
    <button
      onClick={() => setTab(id)}
      className={`flex-1 py-3 text-xs font-bold transition border-b-2 ${active ? `${tone} border-current` : "text-stone-500 border-transparent hover:text-stone-300"}`}
      data-testid={`tab-${id}`}
    >
      {label}
    </button>
  );
}

// ============== Session Tab ==============
function SessionTab({ session, companyId, onChange, onClose }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [desc] = useState("");  // kept for backwards-compat with endWorkSession signature; textarea removed
  const [busy, setBusy] = useState(false);
  // 409 collision modal — { blocker_name, blocker_id, requested_name }
  const [collision, setCollision] = useState(null);
  // Per-session multiplier override (2026-05-20). null = use default 3×;
  // 1 = charge client 1× the Emergent cost; 2 = charge 2×.
  const [multOverride, setMultOverride] = useState(null);

  const startNow = async () => {
    const n = parseFloat(start);
    if (isNaN(n) || n < 0) { toast.error("הזן כמות קרדיטים תקינה"); return; }
    setBusy(true);
    try {
      await startWorkSession(companyId, n);
      toast.success(`סשן עבודה התחיל — ${n} קרדיטים בהתחלה`);
      setStart("");
      await onChange();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === "object") {
        setCollision({
          blocker_name: detail.blocking_company?.name || "לקוח אחר",
          blocker_id: detail.blocking_company?.id,
          requested_name: detail.requested_company?.name || "לקוח חדש",
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "שגיאה");
      }
    }
    finally { setBusy(false); }
  };

  const endNow = async () => {
    const n = parseFloat(end);
    if (isNaN(n) || n < 0) { toast.error("הזן כמות קרדיטים תקינה"); return; }
    const startV = session?.start_credits ?? 0;
    if (n > startV) { toast.error("הסכום הסופי גדול מהסכום ההתחלתי"); return; }
    setBusy(true);
    try {
      const res = await endWorkSession(companyId, n, desc, multOverride);
      toast.success(`חויב ${res.operation.charged_credits} קרדיטים מהלקוח`);
      setEnd("");
      setMultOverride(null);
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
    finally { setBusy(false); }
  };

  const cancelNow = async () => {
    if (!window.confirm("לבטל את הסשן ללא חיוב?")) return;
    try { await cancelWorkSession(companyId); toast.success("בוטל"); await onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
  };

  if (session) {
    const endN = parseFloat(end);
    const valid = !isNaN(endN) && endN >= 0 && endN <= (session.start_credits ?? 0);
    const raw = valid ? +(session.start_credits - endN).toFixed(2) : 0;
    // Resolve effective multiplier for the live preview.
    const effectiveMult = multOverride === 1 || multOverride === 2 ? multOverride : AI_COST_MULTIPLIER;
    const charged = +(raw * effectiveMult).toFixed(2);
    return (
      <div className="space-y-4" data-testid="session-tab-active">
        <div className="rounded-xl bg-gradient-to-br from-emerald-950/60 to-emerald-900/40 border border-emerald-500/40 p-4">
          <div className="flex items-center gap-2 mb-2">
            <PlayCircle className="h-5 w-5 text-emerald-300 animate-pulse" />
            <span className="font-extrabold text-emerald-200 text-sm">סשן עבודה פעיל</span>
          </div>
          <div className="text-xs text-stone-300 space-y-0.5">
            <div>התחיל: <strong className="text-white">{new Date(session.started_at).toLocaleString("he-IL")}</strong></div>
            <div>כמות קרדיט התחלתית: <strong className="text-white">{session.start_credits}</strong></div>
          </div>
        </div>

        {/* AI auto-summarizer — replaces the old manual category grid.
            When SA ends the session, the desc field below is fed to
            Claude Haiku which produces 2-4 categorized activity bullets
            embedded in the credit_operation, so the client sees a clear
            "what was done" list inside the chevron-expand in their
            billing history. No manual entry needed. */}

        <div className="rounded-xl bg-stone-800 border border-rose-500/30 p-4 space-y-3">
          <div className="text-[11px] font-extrabold text-rose-300 uppercase tracking-widest">סיים עבודה</div>
          <label className="block">
            <span className="text-[11px] font-bold text-stone-300">כמות קרדיט בסיום עבודה (Emergent)</span>
            <input
              type="number" step="0.01" min="0"
              value={end} onChange={(e) => setEnd(e.target.value)}
              placeholder={String(session.start_credits)}
              className="mt-1 w-full bg-stone-900 border border-white/10 rounded-lg px-3 py-2 text-white text-lg font-bold" dir="ltr"
              data-testid="session-end-input"
            />
          </label>

          {/* Per-session multiplier override (2026-05-20).
              Default = ×3 (= 1.80₪/קרדיט). ×2 = 1.20₪/קרדיט. ×1 = 0.60₪/
              קרדיט (no profit, same as Emergent cost). Mutually-exclusive
              radio-style checkboxes; selecting one auto-deselects the other. */}
          <div className="rounded-lg bg-stone-900/60 border border-white/10 p-3 space-y-2">
            <div className="text-[10px] font-extrabold text-stone-400 uppercase tracking-widest">
              חישוב חיוב (ברירת מחדל: ×3 = 1.80₪/קרדיט)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-md border transition ${
                  multOverride === 2
                    ? "bg-fuchsia-500/20 border-fuchsia-400/60 text-fuchsia-100"
                    : "bg-stone-800 border-white/5 text-stone-300 hover:border-fuchsia-400/30"
                }`}
                data-testid="session-mult-x2-label"
              >
                <input
                  type="checkbox"
                  checked={multOverride === 2}
                  onChange={(e) => setMultOverride(e.target.checked ? 2 : null)}
                  className="h-3.5 w-3.5 accent-fuchsia-500"
                  data-testid="session-mult-x2"
                />
                <div className="flex flex-col leading-tight">
                  <span className="text-xs font-bold">חייב <strong>×2</strong></span>
                  <span className="text-[10px] opacity-70">1.20₪/קרדיט</span>
                </div>
              </label>
              <label
                className={`flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-md border transition ${
                  multOverride === 1
                    ? "bg-sky-500/20 border-sky-400/60 text-sky-100"
                    : "bg-stone-800 border-white/5 text-stone-300 hover:border-sky-400/30"
                }`}
                data-testid="session-mult-x1-label"
              >
                <input
                  type="checkbox"
                  checked={multOverride === 1}
                  onChange={(e) => setMultOverride(e.target.checked ? 1 : null)}
                  className="h-3.5 w-3.5 accent-sky-500"
                  data-testid="session-mult-x1"
                />
                <div className="flex flex-col leading-tight">
                  <span className="text-xs font-bold">חייב <strong>×1</strong></span>
                  <span className="text-[10px] opacity-70">0.60₪/קרדיט · ללא רווח</span>
                </div>
              </label>
            </div>
            {multOverride && (
              <p className="text-[10px] text-amber-300 italic">
                עוקפים את ברירת המחדל לסשן הזה בלבד — חיוב ×{multOverride} ({(multOverride * 0.6).toFixed(2)}₪/קרדיט) במקום ×3
              </p>
            )}
          </div>

          {valid && (
            <div className="rounded-lg bg-rose-950/40 border border-rose-500/30 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-stone-300">הוצאת בסשן:</span>
                <span className="font-extrabold text-white text-base">{raw} קרדיטים</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-rose-300 font-bold">סה"כ ירידה אצל הלקוח:</span>
                <span className="font-extrabold text-rose-200 text-lg">−{charged} ({formatNis(charged * CREDIT_TO_NIS)})</span>
              </div>
              <p className="text-[9px] text-stone-500 italic mt-1.5">
                (החישוב מוכפל ב-{effectiveMult} אוטומטית — הלקוח לא רואה את החישוב הזה)
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={endNow} disabled={busy || !valid}
              className="flex-1 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 transition"
              data-testid="session-end-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><StopCircle className="h-4 w-4" /> סיים עבודה</>}
            </button>
            <button onClick={cancelNow} className="bg-stone-700 hover:bg-stone-600 text-stone-300 text-xs font-bold px-4 rounded-lg" data-testid="session-cancel">
              בטל
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No active session — show "Start work" form
  return (
    <div className="rounded-xl bg-gradient-to-br from-fuchsia-950/60 to-indigo-950/60 border border-fuchsia-500/40 p-5" data-testid="session-tab-idle">
      <div className="flex items-center gap-2 mb-3">
        <PlayCircle className="h-5 w-5 text-fuchsia-300" />
        <span className="font-extrabold text-fuchsia-200">התחל עבודה לעסק הזה</span>
      </div>
      <p className="text-xs text-stone-300 leading-relaxed mb-4">
        בדוק בפרופיל ה-Emergent שלך כמה קרדיטים יש לך כרגע ורשום פה.
      </p>
      <label className="block mb-3">
        <span className="text-[11px] font-bold text-stone-300">כמות קרדיט התחלתית</span>
        <input
          type="number" step="0.01" min="0"
          value={start} onChange={(e) => setStart(e.target.value)}
          placeholder="1000"
          className="mt-1 w-full bg-stone-900 border border-white/10 rounded-lg px-3 py-2 text-white text-lg font-bold" dir="ltr"
          data-testid="session-start-input"
        />
      </label>
      <button onClick={startNow} disabled={busy}
        className="w-full bg-gradient-to-l from-fuchsia-500 to-indigo-500 hover:opacity-90 disabled:opacity-50 text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2 transition"
        data-testid="session-start-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4" /> התחל לעבוד</>}
      </button>
      {collision && (
        <SessionCollisionModal
          collision={collision}
          onClose={() => setCollision(null)}
          onGoToBlocker={async () => {
            // Close the current modal and navigate to S.ADMIN with the
            // blocking company's session modal pre-opened via query param.
            setCollision(null);
            onClose?.();
            // Use a window navigation hash so other instances of the
            // dashboard can pick this up and auto-open the modal.
            window.location.href = `/S.ADMIN?open-session=${collision.blocker_id}`;
          }}
        />
      )}
    </div>
  );
}

// ============== Session Collision Modal ==============
//
// Shown when the super-admin tries to open a work session for company B
// while company A still has an open session. Offers a quick jump to A so
// the user can close it first.
function SessionCollisionModal({ collision, onClose, onGoToBlocker }) {
  return (
    <div
      className="fixed inset-0 z-[10003] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        data-testid="session-collision-modal"
        className="bg-gradient-to-br from-stone-900 to-rose-950/40 border-2 border-amber-500/60 rounded-2xl shadow-2xl shadow-amber-500/20 max-w-md w-full overflow-hidden"
      >
        <div className="bg-gradient-to-l from-amber-500/20 to-rose-500/20 px-5 py-3 border-b border-amber-500/40 flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-amber-500/30 flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-amber-300" />
          </div>
          <h3 className="font-heading text-base font-extrabold text-amber-100">סשן כבר פתוח</h3>
        </div>
        <div className="px-5 py-4 space-y-3 text-stone-200">
          <p className="text-sm leading-relaxed">
            יש לך כבר סשן עבודה פתוח עבור
            <span className="mx-1 inline-flex items-center gap-1 rounded-full bg-amber-500/20 border border-amber-400/40 px-2 py-0.5 text-amber-100 font-extrabold text-xs">
              {collision.blocker_name}
            </span>
          </p>
          <p className="text-sm text-stone-300 leading-relaxed">
            נא לסגור אותו לפני המשך עבודה עם
            <span className="mx-1 inline-flex items-center gap-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 px-2 py-0.5 text-fuchsia-100 font-extrabold text-xs">
              {collision.requested_name}
            </span>
          </p>
        </div>
        <div className="px-5 py-3 bg-stone-950/60 border-t border-white/5 flex gap-2 flex-row-reverse">
          <button
            type="button"
            onClick={onGoToBlocker}
            className="flex-1 bg-gradient-to-l from-amber-500 to-orange-500 hover:opacity-90 text-stone-900 font-extrabold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 transition shadow-lg shadow-amber-500/30"
            data-testid="goto-blocker-session-btn"
          >
            עבור לסשן הפתוח →
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-stone-800 hover:bg-stone-700 text-stone-300 text-sm font-bold px-4 py-2.5 rounded-lg"
            data-testid="dismiss-collision-btn"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );
}

// ============== Approvals Tab ==============
function ApprovalsTab({ pending, onChange }) {
  if (pending.length === 0) {
    return (
      <div className="rounded-xl bg-stone-800 border border-white/5 p-8 text-center text-sm text-stone-500" data-testid="approvals-empty">
        <CheckCircle2 className="h-8 w-8 inline mb-2 text-emerald-400/60" /><br />
        אין בקשות טעינה ממתינות
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="approvals-list">
      {pending.map((t) => (
        <ApprovalRow key={t.id} topup={t} onChange={onChange} />
      ))}
    </div>
  );
}

function ApprovalRow({ topup, onChange }) {
  const [busy, setBusy] = useState(false);
  // Discount slider — 0-100%. Picks an integer percentage; default 0
  // (no discount). When >0, applying-approve bonus-tops-up the client
  // with extra credits equal to `discount% × credits` so the effective
  // cost is reduced. Stored on the topup's note for audit.
  const [discount, setDiscount] = useState(0);
  const baseCredits = Number(topup.credits) || 0;
  const baseNis = Number(topup.amount_nis) || 0;
  const finalNis = Math.max(0, +(baseNis * (1 - discount / 100)).toFixed(2));
  const bonusCredits = Math.round(baseCredits * (discount / 100));

  const act = async (fn, msg, isApprove) => {
    setBusy(true);
    try {
      await fn(topup.id);
      // If a discount was set and we just approved, also push a bonus
      // top-up so the client keeps the full credit total but for a
      // lower paid amount (the discount is effectively "free" credits).
      if (isApprove && discount > 0 && bonusCredits > 0) {
        try {
          await adminTopup(topup.company_id, bonusCredits, "manual",
            `קופון הנחה ${discount}% — בונוס +${bonusCredits} ק׳ על בסיס ${baseCredits}`);
        } catch (e) {
          toast.error("ההנחה לא נרשמה: " + (e.response?.data?.detail || "שגיאה"));
        }
      }
      toast.success(msg);
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-amber-950/30 border border-amber-500/30 p-3 space-y-2" data-testid={`approval-row-${topup.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-extrabold text-white">+{baseCredits} קרדיטים</div>
          <div className="text-[11px] text-stone-400">{formatNis(baseNis)} · {TOPUP_METHODS.find((m) => m.key === topup.method)?.label || topup.method}</div>
        </div>
        <div className="text-[10px] text-amber-300 inline-flex items-center gap-1">
          <Clock className="h-3 w-3" /> ממתין
        </div>
      </div>
      {topup.note && <div className="text-[11px] text-amber-200 italic truncate">{topup.note}</div>}

      {/* Discount coupon slider — 0–100%. */}
      <div className="rounded-lg bg-stone-800/60 border border-fuchsia-500/30 p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-bold text-fuchsia-200" htmlFor={`disc-${topup.id}`}>
            קופון הנחה
          </label>
          <span className="text-[12px] font-extrabold text-fuchsia-100 tabular-nums">{discount}%</span>
        </div>
        <input
          id={`disc-${topup.id}`}
          type="range" min={0} max={100} step={5}
          value={discount}
          onChange={(e) => setDiscount(Number(e.target.value))}
          className="w-full accent-fuchsia-500"
          data-testid={`approval-discount-${topup.id}`}
        />
        {discount > 0 && (
          <div className="text-[10px] text-fuchsia-200 mt-1.5 flex items-center justify-between">
            <span>ללקוח לחיוב: <strong className="text-white">{formatNis(finalNis)}</strong></span>
            <span>בונוס: <strong className="text-emerald-300">+{bonusCredits} ק׳</strong></span>
          </div>
        )}
      </div>

      <div className="flex gap-1.5">
        <button onClick={() => act(approveTopup, "אושר", true)} disabled={busy} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold py-1.5 rounded inline-flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`approval-approve-${topup.id}`}>
          <CheckCircle2 className="h-3 w-3" /> אשר{discount > 0 ? ` (-${discount}%)` : ""}
        </button>
        <button onClick={() => act(rejectTopup, "נדחה", false)} disabled={busy} className="flex-1 bg-stone-700 hover:bg-stone-600 text-stone-200 text-xs font-bold py-1.5 rounded inline-flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`approval-reject-${topup.id}`}>
          <XCircle className="h-3 w-3" /> דחה
        </button>
      </div>
    </div>
  );
}

// ============== Manual Tab ==============
function ManualTab({ companyId, onChange }) {
  const [credits, setCredits] = useState("");
  const [note, setNote] = useState("");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Reset section (owner spec 2026-06-15g) — wipe ALL credit history and
  // seed a single fresh top-up with the exact credits + ILS price.
  const [resetCredits, setResetCredits] = useState("");
  const [resetPrice, setResetPrice] = useState("");
  const [resetNote, setResetNote] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const doReset = async () => {
    const c = parseFloat(resetCredits);
    const p = parseFloat(resetPrice);
    if (isNaN(c) || c < 0) { toast.error("כמות קרדיטים לא תקינה"); return; }
    if (isNaN(p) || p < 0) { toast.error("מחיר לא תקין"); return; }
    const confirmed = window.confirm(
      `איפוס מלא של קרדיטים\n\n` +
      `כל ההיסטוריה הפיננסית של החברה הזו תימחק:\n` +
      `• כל רכישות הקרדיטים\n` +
      `• כל פעולות הזיכוי/חיוב\n` +
      `• כל סשני העבודה\n\n` +
      `ובמקום תוכנס שורה חדשה:\n` +
      `• ${c} קרדיטים\n` +
      `• בעלות ${p} ש"ח\n\n` +
      `פעולה זו לא ניתנת לביטול. להמשיך?`,
    );
    if (!confirmed) return;
    setResetBusy(true);
    try {
      await adminResetCredits(companyId, { credits: c, price_ils: p, note: resetNote });
      toast.success(`איפוס הושלם — ${c} קרדיטים @ ${p} ש"ח`);
      setResetCredits(""); setResetPrice(""); setResetNote("");
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה באיפוס"); }
    finally { setResetBusy(false); }
  };

  const doTopup = async () => {
    const n = parseFloat(credits);
    if (isNaN(n) || n <= 0) { toast.error("כמות לא תקינה"); return; }
    setBusy(true);
    try {
      await adminTopup(companyId, { credits: n, method: "manual", note });
      toast.success(`נוספו ${n} קרדיטים`);
      setCredits(""); setNote("");
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
    finally { setBusy(false); }
  };

  const doAdjust = async () => {
    const d = parseFloat(delta);
    if (isNaN(d) || d === 0) { toast.error("הזן סכום שונה מ-0"); return; }
    if (!reason.trim()) { toast.error("נא לתאר סיבה"); return; }
    setBusy(true);
    try {
      await adjustCredits(companyId, { delta_credits: d, reason });
      toast.success("הותאם");
      setDelta(""); setReason("");
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-stone-800 border border-emerald-500/30 p-4 space-y-2" data-testid="manual-topup">
        <div className="text-[11px] font-extrabold text-emerald-300 uppercase tracking-widest">טעינה ידנית</div>
        <input type="number" min="0" value={credits} onChange={(e) => setCredits(e.target.value)} placeholder="כמות קרדיטים" className="w-full bg-stone-900 border border-white/10 rounded-md px-3 py-2 text-white font-bold" dir="ltr" data-testid="manual-topup-credits" />
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="הערה (אופציונלי)" className="w-full bg-stone-900 border border-white/10 rounded-md px-3 py-2 text-white text-xs" data-testid="manual-topup-note" />
        <button onClick={doTopup} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-bold py-2 rounded-md inline-flex items-center justify-center gap-1.5" data-testid="manual-topup-submit">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> הוסף קרדיטים</>}
        </button>
      </div>

      <div className="rounded-xl bg-stone-800 border border-white/10 p-4 space-y-2" data-testid="manual-adjust">
        <div className="text-[11px] font-extrabold text-stone-300 uppercase tracking-widest">התאמה ידנית (+/−)</div>
        <input type="number" step="0.01" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="+50 או -25" className="w-full bg-stone-900 border border-white/10 rounded-md px-3 py-2 text-white font-bold" dir="ltr" data-testid="manual-adjust-delta" />
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="סיבת התאמה" className="w-full bg-stone-900 border border-white/10 rounded-md px-3 py-2 text-white text-xs" data-testid="manual-adjust-reason" />
        <button onClick={doAdjust} disabled={busy} className="w-full bg-stone-600 hover:bg-stone-500 disabled:opacity-50 text-white text-sm font-bold py-2 rounded-md inline-flex items-center justify-center gap-1.5" data-testid="manual-adjust-submit">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><SettingsIcon className="h-3.5 w-3.5" /> התאם</>}
        </button>
      </div>

      {/* Owner spec 2026-06-15g — Full Reset of credits + history */}
      <div className="rounded-xl bg-rose-950/40 border-2 border-rose-500/50 p-4 space-y-2" data-testid="manual-reset">
        <div className="text-[11px] font-extrabold text-rose-300 uppercase tracking-widest flex items-center gap-1.5">
          ⚠ איפוס מלא — מוחק היסטוריה ויוצר שורה חדשה
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min="0" value={resetCredits} onChange={(e) => setResetCredits(e.target.value)} placeholder="כמות קרדיטים" className="bg-stone-900 border border-rose-500/30 rounded-md px-3 py-2 text-white font-bold" dir="ltr" data-testid="manual-reset-credits" />
          <input type="number" min="0" step="0.01" value={resetPrice} onChange={(e) => setResetPrice(e.target.value)} placeholder='מחיר (ש"ח)' className="bg-stone-900 border border-rose-500/30 rounded-md px-3 py-2 text-white font-bold" dir="ltr" data-testid="manual-reset-price" />
        </div>
        <input type="text" value={resetNote} onChange={(e) => setResetNote(e.target.value)} placeholder="הערה (אופציונלי)" className="w-full bg-stone-900 border border-rose-500/30 rounded-md px-3 py-2 text-white text-xs" data-testid="manual-reset-note" />
        <button onClick={doReset} disabled={resetBusy} className="w-full bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-extrabold py-2.5 rounded-md inline-flex items-center justify-center gap-1.5" data-testid="manual-reset-submit">
          {resetBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>🔄 אפס וטען מחדש</>}
        </button>
      </div>
    </div>
  );
}

// ============== Brief Tab (super-admin reads the questionnaire) ==============
// Safe wrapper: any unexpected error inside BriefTab is caught so the
// whole modal does not crash (this used to white-screen the modal on
// legacy briefs that stored objects in string fields).
class BriefTabSafe extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[BriefTab] render error:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="text-center py-10 text-sm text-rose-300 space-y-3" data-testid="brief-error">
          <XCircle className="h-8 w-8 inline text-rose-400" />
          <div className="font-extrabold">לא ניתן להציג את האפיון</div>
          <div className="text-xs text-stone-400 max-w-md mx-auto leading-relaxed">
            הנתונים בתיק האפיון של הלקוח לא בפורמט תקין. בקש מהלקוח למלא את האפיון מחדש דרך הצ׳אט.
          </div>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="rounded-lg bg-stone-700 hover:bg-stone-600 px-3 py-1.5 text-xs font-bold text-white"
            data-testid="brief-error-retry"
          >
            נסה שוב
          </button>
        </div>
      );
    }
    return <BriefTab {...this.props} />;
  }
}

function BriefTab({ companyId, company, onChanged }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/brief`);
        setBrief((data && data.brief) || {});
      } catch (e) {
        // Don't crash the modal — show the empty state instead.
        setBrief({});
      } finally { setLoading(false); }
    })();
  }, [companyId]);

  // Safe helpers: brief fields can occasionally arrive as a plain string
  // (legacy data) or null. Force them into the expected shape so .map/.join
  // never throw at render time.
  const asArray = (v) => (Array.isArray(v) ? v : []);
  const featuresStr = asArray(brief?.features).join(", ");
  const refsStr = asArray(brief?.reference_urls)
    .map((r) => (typeof r === "string" ? r : r?.url))
    .filter(Boolean)
    .join(" · ");
  const filesCount = asArray(brief?.files).length || "";

  const approved = !!company?.brief_approved;
  const approvedAt = company?.brief_approved_at;
  const accessCode = company?.access_code || "";

  const doApprove = async (val) => {
    if (!companyId) return;
    setApproving(true);
    try {
      await api.post(`/admin/companies/${companyId}/approve-brief`, { approved: val });
      toast.success(val ? "האפיון אושר ✓" : "האישור בוטל");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    } finally {
      setApproving(false);
    }
  };

  const copyAccessCode = async () => {
    if (!accessCode) return;
    try {
      await navigator.clipboard.writeText(accessCode);
      setCopied(true);
      toast.success("הקוד הועתק — שלח לי אותו בצ׳אט");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("לא הצלחתי להעתיק");
    }
  };

  if (loading) {
    return <div className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin inline text-stone-400" /></div>;
  }
  if (!brief || Object.keys(brief).length === 0) {
    return (
      <div className="text-center py-10 text-sm text-stone-500" data-testid="brief-empty">
        <ClipboardList className="h-8 w-8 inline mb-2 text-stone-600" /><br />
        הלקוח עדיין לא מילא אפיון
      </div>
    );
  }

  // ---- Build the 7 clean summary steps the user asked for ----
  // Each step shows ONLY filled-in data; empty steps render a tidy "—".
  const stepBlocks = [
    {
      n: 1,
      title: "מה העסק עושה",
      rows: [
        ["שם העסק", company?.display_name || company?.name],
        ["מה העסק עושה", brief.business_what],
        ["תחום", brief.business_industry],
      ],
    },
    {
      n: 2,
      title: "הסיפור והקהל",
      rows: [
        ["הסיפור של העסק", brief.business_about],
        ["קהל היעד", brief.target_audience],
        ["סגנון השפה", brief.tone_of_voice],
      ],
    },
    {
      n: 3,
      title: "מיתוג ועיצוב",
      rows: [
        ["צבע ראשי", brief.primary_color],
        ["צבע משני", brief.secondary_color],
        ["צבע אקסנט", brief.accent_color],
        ["פונט", brief.font_family],
        ["סוג כפתורים", brief.button_style],
      ],
    },
    {
      n: 4,
      title: "שירותים ופיצ׳רים",
      rows: [
        ["שירותים מרכזיים", brief.key_services],
        ["פיצ׳רים שנבחרו", featuresStr],
      ],
    },
    {
      n: 5,
      title: "רשתות חברתיות וקבצים",
      rows: [
        ["Facebook", brief.facebook_url],
        ["Instagram", brief.instagram_url],
        ["TikTok", brief.tiktok_url],
        ["מספר קבצים", filesCount],
        ["אתרים מועדפים", refsStr],
      ],
    },
    {
      n: 6,
      title: "בקשות אוטומציה",
      rows: [
        ["מה הלקוח רוצה לאוטמט", brief.automation_needs || brief.automations_wanted],
        ["נקודות כאב", asArray(brief.automation_pains).join(" · ")],
      ],
    },
    {
      n: 7,
      title: "סיום ושמירה",
      rows: [
        ["נשלח בתאריך", brief.finished_at ? new Date(brief.finished_at).toLocaleString("he-IL") : ""],
        ["סטטוס", brief.finished ? "✓ הלקוח שלח את האפיון" : "⏳ עדיין לא נשלח"],
      ],
    },
  ];

  return (
    <div className="space-y-4" data-testid="brief-tab">
      {/* Top approval bar */}
      <div className="rounded-2xl border border-white/10 bg-stone-800 p-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="text-stone-400">לקוח:</span>{" "}
          <strong className="text-white" data-testid="brief-customer-name">{company?.display_name || company?.name || "—"}</strong>
        </div>
        {brief.finished && (
          approved ? (
            <button
              type="button"
              onClick={() => doApprove(false)}
              disabled={approving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/50 px-3 py-1.5 text-xs font-extrabold text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50"
              data-testid="unapprove-brief-btn"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              מאושר {approvedAt ? `· ${new Date(approvedAt).toLocaleDateString("he-IL")}` : ""}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => doApprove(true)}
              disabled={approving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-stone-900 font-extrabold px-3 py-1.5 text-xs shadow-md disabled:opacity-50"
              data-testid="approve-brief-btn"
            >
              {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              אשר איפיון
            </button>
          )
        )}
      </div>

      {/* 7 numbered step cards — one below the other */}
      <ol className="space-y-2.5" data-testid="brief-steps">
        {stepBlocks.map((s) => (
          <BriefStepCard key={s.n} step={s} />
        ))}
      </ol>

      {/* Sharable brief identifier (access_code) — copy this back to me */}
      <div className="rounded-2xl border-2 border-dashed border-fuchsia-500/40 bg-fuchsia-950/30 p-4 space-y-2" data-testid="brief-share-block">
        <div className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-300">קוד זיהוי האפיון — שלח לי כאן בצ׳אט</div>
        <button
          type="button"
          onClick={copyAccessCode}
          disabled={!accessCode}
          className="w-full flex items-center justify-between gap-3 rounded-xl bg-stone-900 border border-fuchsia-500/40 hover:border-fuchsia-300 px-4 py-3 transition group disabled:opacity-50"
          data-testid="brief-share-copy-btn"
        >
          <span className="font-mono font-extrabold text-fuchsia-200 text-base sm:text-lg" dir="ltr">{accessCode || "—"}</span>
          <span className="text-[11px] text-fuchsia-300 group-hover:text-fuchsia-100">
            {copied ? "הועתק ✓" : "לחץ להעתקה"}
          </span>
        </button>
        <p className="text-[11px] text-stone-400 leading-relaxed">
          העתק את הקוד הזה והדבק לי בצ׳אט. אזהה אוטומטית את הלקוח, אציג לך את שמו לאישור — ובמילה שלך אקים לו את המערכת.
        </p>
      </div>
    </div>
  );
}

// Single numbered step card — only renders rows that have a value.
// Values are coerced to safe strings so legacy briefs (which sometimes
// stored objects in fields that should be strings) cannot crash render.
function _coerce(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return ""; }
}
function BriefStepCard({ step }) {
  const rows = (step.rows || []).map(([k, v]) => [k, _coerce(v)]);
  const filled = rows.filter((r) => r[1] !== "" && r[1] !== null && r[1] !== undefined);
  if (filled.length === 0) {
    return (
      <li className="rounded-xl bg-stone-800/40 border border-white/5 p-3" data-testid={`brief-step-${step.n}`}>
        <div className="flex items-center gap-2">
          <span className="h-6 w-6 rounded-full bg-stone-700 text-stone-400 text-xs font-extrabold flex items-center justify-center">{step.n}</span>
          <span className="text-stone-400 text-sm font-bold">{step.title}</span>
          <span className="text-stone-500 text-xs">—</span>
        </div>
      </li>
    );
  }
  return (
    <li className="rounded-xl bg-stone-800 border border-white/10 p-3" data-testid={`brief-step-${step.n}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="h-6 w-6 rounded-full bg-fuchsia-500/20 text-fuchsia-200 text-xs font-extrabold flex items-center justify-center">{step.n}</span>
        <span className="text-white text-sm font-bold">{step.title}</span>
      </div>
      <dl className="space-y-1.5 ps-8">
        {filled.map(([k, v], i) => (
          <div key={i} className="grid grid-cols-[110px_1fr] gap-2 items-start text-xs">
            <dt className="text-stone-400 font-bold leading-relaxed">{k}</dt>
            <dd className="text-stone-100 leading-relaxed whitespace-pre-wrap break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </li>
  );
}

function Field({ label, value, long }) {
  if (!value) return null;
  return (
    <div className="rounded-xl bg-stone-800 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-1">{label}</div>
      <div className={`text-stone-200 whitespace-pre-wrap ${long ? "text-xs leading-relaxed" : "text-sm font-bold"}`}>{value}</div>
    </div>
  );
}

function SocialRow({ label, url }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-fuchsia-300 hover:text-fuchsia-200">
      <span className="font-bold text-stone-300">{label}:</span>
      <span dir="ltr" className="truncate">{url}</span>
    </a>
  );
}

// ============== History Tab (super-admin sees full detail incl. session times) ==============
function HistoryTab({ ops, topups }) {
  // unify sessions / charges / topups into one chronological ledger
  const sessionOps = ops.filter((o) => o.session_id);
  const otherOps = ops.filter((o) => !o.session_id);
  const items = [
    ...sessionOps.map((o) => ({
      id: `session-${o.id}`,
      created_at: o.created_at,
      type: "session",
      label: o.description || "סשן עבודה",
      raw: o.raw_credits, charged: o.charged_credits,
      session_start: o.session_start_balance, session_end: o.session_end_balance,
      activities: o.activities || [],
    })),
    ...otherOps.map((o) => ({
      id: `op-${o.id}`,
      created_at: o.created_at,
      type: o.is_adjustment ? "adjust" : "charge",
      label: o.description || "פעולה",
      charged: o.charged_credits,
      activities: [],
    })),
    ...topups.map((t) => ({
      id: `tp-${t.id}`,
      created_at: t.created_at,
      type: "topup",
      label: `טעינה (${TOPUP_METHODS.find((m) => m.key === t.method)?.label || t.method})`,
      credits: t.credits,
      status: t.status,
      activities: [],
    })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (items.length === 0) {
    return <div className="text-center py-10 text-sm text-stone-500">אין רשומות</div>;
  }
  return (
    <div className="space-y-1.5" data-testid="history-tab">
      {items.map((h) => <HistoryItem key={h.id} h={h} />)}
    </div>
  );
}

function HistoryItem({ h }) {
  // Sessions with activities can be expanded to reveal a checklist of
  // what happened during that work session (productions added, payments
  // recorded, etc.). Other rows render compactly without expansion.
  const [open, setOpen] = useState(false);
  const hasActivities = (h.activities || []).length > 0;
  return (
    <div className={`rounded-lg p-3 border ${
      h.type === "session" ? "bg-emerald-950/20 border-emerald-500/20" :
      h.type === "topup" ? `bg-amber-950/20 border-amber-500/20 ${h.status !== "approved" ? "opacity-60" : ""}` :
      h.type === "adjust" ? "bg-stone-800 border-white/10" :
      "bg-rose-950/20 border-rose-500/20"
    }`} data-testid={`history-item-${h.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-white truncate">{h.label}</div>
          <div className="text-[10px] text-stone-400">
            {new Date(h.created_at).toLocaleString("he-IL")}
            {h.type === "session" && (<> · יתרת Emergent: <strong>{h.session_start}</strong> → <strong>{h.session_end}</strong> · גלם: {h.raw} × 2</>)}
            {h.type === "topup" && h.status !== "approved" && <> · {h.status === "pending" ? "ממתין" : "נדחה"}</>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasActivities && (
            <button
              type="button"
              onClick={() => setOpen((s) => !s)}
              className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-300 hover:bg-emerald-500/15 px-1.5 py-0.5 rounded"
              data-testid={`history-toggle-${h.id}`}
            >
              {h.activities.length} ✓
              {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}
          <div className={`text-base font-extrabold ${
            h.type === "topup" && h.status === "approved" ? "text-emerald-300" :
            h.type === "adjust" ? (h.charged < 0 ? "text-emerald-300" : "text-rose-300") :
            "text-rose-300"
          }`}>
            {h.type === "topup" ? `+${h.credits}` :
              h.type === "adjust" ? `${h.charged < 0 ? "+" : "−"}${Math.abs(h.charged)}` :
              `−${h.charged}`}
          </div>
        </div>
      </div>
      {open && hasActivities && (
        <ul className="mt-2 pt-2 border-t border-emerald-500/20 space-y-1" data-testid={`history-activities-${h.id}`}>
          {h.activities.map((a, idx) => (
            <li key={a.id || idx} className="flex items-start gap-1.5 text-[11px] text-emerald-100">
              <CheckCircle2 className="h-3 w-3 text-emerald-400 flex-shrink-0 mt-0.5" />
              <span className="flex-1">{a.summary}</span>
              <span className="text-stone-500 text-[9px] tabular-nums flex-shrink-0">{new Date(a.at).toLocaleTimeString("he-IL").slice(0, 5)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ============== Live Session Activity Log (super admin) ==============
//
// Lets the super-admin record human-readable work items during an active
// session — e.g. "נוסף כפתור ספקים", "תוקן באג בתיק אפיון". These activities
// are persisted with the session_id and harvested into the credit operation
// when the session ends, so the client sees a clear "what was done" list in
// their charges history.
// 9 pre-built categories — covers ~95% of agency work. Click → either
// auto-add with the default summary, or open the short-text input.
const ACTIVITY_CATEGORIES = [
  { id: "design",      label: "עיצוב",            icon: "🎨", color: "from-pink-500 to-rose-500" },
  { id: "feature",     label: "פיצ׳ר חדש",        icon: "✨", color: "from-violet-500 to-fuchsia-500" },
  { id: "bug",         label: "תיקון באג",         icon: "🐛", color: "from-amber-500 to-orange-500" },
  { id: "integration", label: "אינטגרציה",         icon: "🔗", color: "from-cyan-500 to-blue-500" },
  { id: "automation",  label: "אוטומציה",          icon: "⚙️", color: "from-emerald-500 to-teal-500" },
  { id: "content",     label: "תוכן/טקסטים",       icon: "📝", color: "from-yellow-500 to-amber-500" },
  { id: "wa",          label: "וואצאפ/הודעות",     icon: "💬", color: "from-green-500 to-emerald-500" },
  { id: "performance", label: "ביצועים/מובייל",    icon: "📱", color: "from-sky-500 to-indigo-500" },
  { id: "other",       label: "אחר",               icon: "•",  color: "from-stone-500 to-stone-600" },
];

function SessionActivityLog({ companyId }) {
  const [items, setItems] = useState([]);
  const [pickedCat, setPickedCat] = useState(null); // { id, label, icon, color }
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const list = await listSessionActivities(companyId);
      setItems(list);
    } catch { /* silent — no active session */ }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [companyId]);

  const submit = async () => {
    if (!pickedCat) return;
    const note = text.trim();
    // Compose "[category] note" — the BillingHistoryModal shows it as-is
    // and the client sees a tidy "✨ פיצ׳ר חדש — הוספנו כפתור ספקים" line.
    const composed = note ? `${pickedCat.label} — ${note}` : pickedCat.label;
    setAdding(true);
    try {
      await addSessionActivity(companyId, composed);
      toast.success("נרשם");
      setText("");
      setPickedCat(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    } finally { setAdding(false); }
  };

  return (
    <div className="rounded-xl bg-stone-800 border border-fuchsia-500/30 p-3 space-y-3" data-testid="session-activity-log">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-extrabold text-fuchsia-300 uppercase tracking-widest flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" />
          רישום פעולות בסשן
        </div>
        <span className="text-[10px] text-stone-500">
          {items.length} פעולות
        </span>
      </div>

      {/* Step 1 — pick category as a chip */}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1.5">
          {pickedCat ? "השלם תיאור קצר (לא חובה)" : "בחר קטגוריה"}
        </div>
        {!pickedCat ? (
          <div className="grid grid-cols-3 gap-1.5">
            {ACTIVITY_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setPickedCat(c)}
                className={`text-[11px] font-bold text-white py-1.5 px-2 rounded-lg bg-gradient-to-l ${c.color} hover:opacity-90 active:scale-[0.97] transition flex items-center justify-center gap-1`}
                data-testid={`activity-cat-${c.id}`}
              >
                <span>{c.icon}</span>
                <span className="truncate">{c.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {/* Picked-category chip + change */}
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-extrabold text-white py-1 px-2.5 rounded-md bg-gradient-to-l ${pickedCat.color} inline-flex items-center gap-1`} data-testid="activity-picked-cat">
                <span>{pickedCat.icon}</span>{pickedCat.label}
              </span>
              <button
                type="button"
                onClick={() => { setPickedCat(null); setText(""); }}
                className="text-[10px] text-stone-400 hover:text-stone-200 underline"
                data-testid="activity-change-cat"
              >
                שנה קטגוריה
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="כמה מילים (אופציונלי)"
                maxLength={80}
                autoFocus
                className="flex-1 bg-stone-900 border border-white/10 rounded-lg px-2.5 py-2 text-white text-xs placeholder:text-stone-600"
                data-testid="session-activity-input"
              />
              <button
                onClick={submit}
                disabled={adding}
                className="bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-50 text-white text-xs font-bold px-3 rounded-lg inline-flex items-center gap-1 shrink-0"
                data-testid="session-activity-add"
              >
                {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                שמור
              </button>
            </div>
          </div>
        )}
      </div>

      {/* List of saved activities */}
      {loading ? (
        <div className="text-center text-stone-500 text-xs py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-[11px] text-stone-500 italic">
          ✨ ה-AI יכתוב אוטומטית בסיום הסשן לפי התיאור שתזין למטה. או — לחץ קטגוריה כדי לרשום ידנית עכשיו.
        </p>
      ) : (
        <ul className="space-y-1">
          {items.map((a, idx) => (
            <li key={a.id || idx} className="flex items-start gap-2 text-[12px] text-stone-200 bg-stone-900/40 rounded-md px-2 py-1.5" data-testid={`session-activity-item-${idx}`}>
              <span className="shrink-0 mt-1 h-1.5 w-1.5 rounded-full bg-fuchsia-400" />
              <span className="flex-1 leading-snug">{a.summary}</span>
              <span className="shrink-0 text-[10px] text-stone-500 tabular-nums">
                {a.at ? new Date(a.at).toLocaleTimeString("he-IL").slice(0, 5) : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
